import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { GlobalContext } from '../../Context/GlobalContext';
import useAxios from '../../Hooks/useAxios';
import MenuListTasks from '../MenuListTasks/MenuListTasks';
import ProcessForm from '../ProcessForm/ProcessForm';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, Typography } from '@mui/material';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { DATE_FORMAT, STATUS_RESOLVED } from '../../Constants/Global';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useLocation } from 'react-router-dom';
import { AxiosService } from '../../Services/AxiosService';

/**
 * A reusable Paper component for the draggable dialog. This component is used to wrap the dialog content
 * and enable dragging functionality.
 * 
 * @component
 * @param {Object} props - The component props.
 * @param {React.RefObject} props.nodeRef - A ref to the Paper component.
 * @returns {JSX.Element} A draggable Paper component.
 */
function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper 
      {...props} 
      ref={nodeRef} 
      className="formPaper"
      sx={{
        overflow: "auto",
        position: "fixed",
        top: "90px",
        maxWidth: "420px",
        borderRadius: "30px"
      }}/>
    </Draggable>
  );
}

/**
 * A popup dialog component for managing process details. It includes a form for updating the process end date
 * and buttons for resolving, removing, or saving changes to the process. The dialog is draggable and integrates
 * with the global context for state management.
 * 
 * @component
 * @returns {JSX.Element} The rendered process popup dialog component.
 */
function ProcessPopup() {
  const {
    processModalOpen, 
    setProcessModalOpen, 
    setModalOpen, 
    modalData, 
    setAlertType, 
    setAlertMessage, 
    setAlertTitle,
    setAlertOpen, 
    setAlertBarSeverity,
    setAlertBarMessage,
    setAlertBarOpen,
    setRefreshTable
  } = React.useContext(GlobalContext);
  const { data, isLoading, error } = useAxios("processDetail", modalData.label? modalData.label : modalData.id);
  const [endDate, setEndDate]= React.useState();
  const [loadingRequest, setLoadingRequest] = React.useState(false);
  const navigate = useNavigate();
  const auth = useAuth();
  const { pathname } = useLocation();

  /**
   * Handles closing the dialog when the user clicks outside, presses the Escape key,
   * or interacts with specific elements.
   * 
   * @param {Event} event - The event triggering the close action.
   */
  const handleClose = (event) => {    
    if (event.nativeEvent.code === "Escape"
      || (event.target.tagName !== "H2"
        && event.target.tagName !== "LI"
        && event.target.tagName !== "DIV")){
      setProcessModalOpen(false);
    }
    setModalOpen(false);
  };

  /**
   * Handles the "Resolve" button click. Displays a confirmation dialog for resolving the process.
   */
  const handleAccept = (event) => {
    setAlertType("resolve_task");
    setAlertTitle(<Typography color='success'><DoneOutlineIcon color="success" sx={{ fontWeight:'fontWeightBold', position: "relative", top: "5px", marginRight: "10px" }} />Resolve task</Typography>);
    setAlertMessage(<Typography>Are you sure you want to solve the task?<br/>This action cannot be undone</Typography>);
    // setModalOpen(false);
    setAlertOpen(true);
  };

  /**
   * Handles the "Remove" button click. Displays a confirmation dialog for removing the process.
   */
  const handleRemove = (event) => {
    setAlertType("remove_task");
    setAlertTitle(<Typography color='error'><DeleteIcon color="error" sx={{ fontWeight:'fontWeightBold', position: "relative", top: "5px", marginRight: "10px" }} />Remove task</Typography>);
    setAlertMessage(<Typography>Are you sure you want to remove the task?<br/>This action cannot be undone</Typography>);
    // setModalOpen(false);
    setAlertOpen(true);
  };

  /**
   * Handles changes to the end date in the process form.
   * 
   * @param {Object} event - The date picker change event.
   */
  const handleDateChange = (event) => {
    setEndDate(event.format(DATE_FORMAT));
  };

  /**
   * Refreshes the current view based on the application's pathname.
   * - For "/calendar" route: Forces a navigation refresh by temporarily redirecting to "/#"
   * - For "/processes" route: Increments the refresh counter to trigger a table re-render
   * - For all other routes: No action is taken
   *
   * @function refreshView
   * @returns {void}
   *
   * @example
   * // When on calendar view:
   * refreshView(); // Will force refresh the calendar
   *
   * @example
   * // When on processes view:
   * refreshView(); // Will increment refresh counter causing table to reload
   */
  const refreshView = () => {
    switch (pathname) {
      case "/calendar":
        navigate("/#");
        break;

      case "/processes":
        setRefreshTable(prev => prev + 1);
        break;

      default:
        break;
    }
  };

  /**
   * Handles the "Save" button click. Saves the updated end date for the process via an API call.
   */
  const handleSave = () => {
      setLoadingRequest(true);
      const requestBody =  {
        endDate: endDate
      };
      console.log(modalData)
      AxiosService.update_task(auth.user?.access_token, modalData.type === "process"? modalData.id : modalData.label, requestBody)
      .then((response) => {
        setAlertBarSeverity("success");
        setAlertBarMessage(response.data.message);
        setAlertBarOpen(true);
        setProcessModalOpen(false);
        refreshView();
      })
      .catch((err) => {
        setAlertBarSeverity("error");
        setAlertBarMessage(`${err.message} (${err.response.statusText}): ${err.response.data.error}`);
        setAlertBarOpen(true);
      })
      .finally(() => {
        setLoadingRequest(false);
      });
  }
  
  // Generate the dialog title based on the process data
  let popupTitle = "";
  if (!isLoading){
    popupTitle = <MenuListTasks item={{...data, type: "process"}} tagType="forms" key={"process" + data.id} />;
  } else {
    popupTitle = <><CircularProgress size="1rem" sx={{marginRight: "4px"}} />Loading...</>;
  }

  return (
    <> 
    <Dialog
      open={processModalOpen}
      onClose={handleClose}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"      
    >
      <DialogTitle style={{ cursor: 'unset' }} id="draggable-dialog-title" onClick={handleClose}>
        {popupTitle}
      </DialogTitle>
    <DialogContent
        sx={{
        maxHeight: '10vh',
        overflowY: 'auto'
        }}>
        {!isLoading && !error && <ProcessForm processData={data} handleDateChange={handleDateChange}></ProcessForm>}
      </DialogContent>
      { !isLoading && 
      <DialogActions> 
        <Button sx={{ fontWeight:'fontWeightBold', margin: "auto"}} onClick={handleClose}>
          <CloseIcon/> Exit
        </Button>
        {data.statusId !== STATUS_RESOLVED && <>
        <Button color="error" sx={{ fontWeight:'fontWeightBold', margin: "auto" }} onClick={handleRemove}>
          <DeleteOutlineIcon /> Remove
        </Button>
        <Button color="success" sx={{ fontWeight:'fontWeightBold', margin: "auto" }} onClick={handleAccept}>
          <DoneIcon /> Resolve
        </Button>
        <Button color="primary" sx={{ fontWeight:'fontWeightBold', margin: "auto" }} onClick={handleSave} loading={loadingRequest}>
          <SaveIcon /> Save
        </Button></>}
      </DialogActions>
      }
    </Dialog>
  </>
  );
}

export default ProcessPopup;
